// Un montant en toutes lettres, comme le demandent les reçus fiscaux (D-124) : « cent quarante-trois euros et
// soixante-dix-neuf centimes ». Orthographe traditionnelle : traits d'union entre dizaines et unités seulement
// (« cent quarante-trois », « deux mille cinq cents »), « et » pour 21, 31… 71 (« soixante et onze »), jamais pour
// 81 et 91 ; « vingts » et « cents » prennent un s quand ils terminent le nombre et sont multipliés (« quatre-vingts »,
// « deux cents »), pas devant « mille » (« deux cent mille ») ; « mille » est invariable, « million » s'accorde
// (« deux millions d'euros »).

const UNITS = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze',
  'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const TENS = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];

/** 0 à 99 ; `last` : le nombre s'arrête là (« quatre-vingts ») ou continue (« quatre-vingt mille »). */
function below100(n: number, last: boolean): string {
  if (n < 20) return UNITS[n]!;
  const t = Math.floor(n / 10), u = n % 10;
  if (t === 7 || t === 9) return `${TENS[t]}${t === 7 && u === 1 ? ' et ' : '-'}${UNITS[10 + u]}`;
  if (u === 0) return t === 8 && last ? 'quatre-vingts' : TENS[t]!;
  if (u === 1 && t !== 8) return `${TENS[t]} et un`;
  return `${TENS[t]}-${UNITS[u]}`;
}

/** 1 à 999. */
function below1000(n: number, last: boolean): string {
  const h = Math.floor(n / 100), r = n % 100;
  const parts: string[] = [];
  if (h === 1) parts.push('cent');
  else if (h > 1) parts.push(`${UNITS[h]} ${r === 0 && last ? 'cents' : 'cent'}`);
  if (r > 0) parts.push(below100(r, last));
  return parts.join(' ');
}

/** Un entier en lettres (0 à 999 999 999). `noun` : suivi d'un nom (« euros »), qui laisse « cents » au pluriel. */
export function integerInWords(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n > 999_999_999) throw new RangeError('montant hors limites');
  if (n === 0) return 'zéro';
  const m = Math.floor(n / 1_000_000), k = Math.floor(n / 1000) % 1000, r = n % 1000;
  const parts: string[] = [];
  if (m > 0) parts.push(`${below1000(m, true)} ${m > 1 ? 'millions' : 'million'}`);
  if (k > 0) parts.push(k === 1 ? 'mille' : `${below1000(k, false)} mille`);
  if (r > 0) parts.push(below1000(r, true));
  return parts.join(' ');
}

/** « cent quarante-trois euros et soixante-dix-neuf centimes », « cinq cents euros », « un euro », « un million d'euros ». */
export function eurosInWords(cents: number): string {
  if (!Number.isInteger(cents) || cents < 0) throw new RangeError('montant invalide');
  const e = Math.floor(cents / 100), c = cents % 100;
  const cs = c ? `${integerInWords(c)} ${c > 1 ? 'centimes' : 'centime'}` : '';
  if (e === 0) return cs || 'zéro euro';
  const ew = integerInWords(e);
  const euro = e % 1_000_000 === 0 ? ` d’euros` : e > 1 ? ' euros' : ' euro';
  return `${ew}${euro}${cs ? ` et ${cs}` : ''}`;
}
